import { useEffect, useMemo, useState } from 'react'
import * as IntegrationHttp from '../../../../../services/api/https/settings/integration.http'
import { getTiposDeArquivosDeImportacao } from '../../../../../services/api/https/system/system.http'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../../store/actions/system.actions'
import { errorHandler } from '../../../../../services/util/error.service'
import { INTEGRATION_PARTNERS } from './integrationPartners'

const INITIAL_STATE = {
  integrations: [],
  fileTypes: [],
  // integration: null = nova integração
  formModal: { show: false, integration: null },
  credentialsIntegrationId: null,
  generatedToken: null,
  // Confirmação pendente: { title, content, onOk }
  confirm: null,
}

// Lógica da seção "Integrações via API (v1)" do cadastro do TP
export default function useIntegrationsSection(tpId, dispatch) {
  const [state, setState] = useState(INITIAL_STATE)

  const localDispatch = useMemo(() => {
    async function run(message, action, successMessage) {
      try {
        dispatch(showLoadingBox(message, 'Aguarde...'))
        await action()
        dispatch(hideLoadingBox())
        if (successMessage) dispatch(showMessageBox(successMessage, null))
      } catch (error) {
        dispatch(hideLoadingBox())
        dispatch(showMessageBox(null, errorHandler(error)))
      }
    }

    async function reload() {
      const integrations = await IntegrationHttp.getTpIntegrations(tpId)
      setState(s => ({ ...s, integrations }))
      return integrations
    }

    return {
      reload: () => run('Carregando integrações...', reload),

      onNewClicked() {
        setState(s => ({ ...s, formModal: { show: true, integration: null } }))
      },

      onEditClicked(integration) {
        setState(s => ({ ...s, formModal: { show: true, integration } }))
      },

      onFormClosed() {
        setState(s => ({ ...s, formModal: { show: false, integration: null } }))
      },

      // Nova integração: cria e já oferece gerar o primeiro token (abre a lista de tokens)
      onFormSaved(integration, partner, config) {
        if (!integration) {
          return run('Criando integração...', async () => {
            const created = await IntegrationHttp.createTpIntegration(tpId, partner, config)
            await reload()
            setState(s => ({
              ...s,
              formModal: { show: false, integration: null },
              credentialsIntegrationId: created.id,
            }))
          })
        }
        // Alteração de integração em uso: vale para as próximas chamadas do parceiro
        return setState(s => ({
          ...s,
          confirm: {
            title: 'Alterar configuração',
            content:
              'A nova configuração passa a valer imediatamente para as próximas chamadas do parceiro. Deseja continuar?',
            onOk: () =>
              run(
                'Salvando configuração...',
                async () => {
                  await IntegrationHttp.updateTpIntegration(tpId, integration.id, { config })
                  await reload()
                  setState(prev => ({ ...prev, formModal: { show: false, integration: null } }))
                },
                'Configuração salva',
              ),
          },
        }))
      },

      onToggleActiveClicked(integration) {
        const activating = !integration.active
        setState(s => ({
          ...s,
          confirm: {
            title: activating ? 'Ativar integração' : 'Desativar integração',
            content: activating
              ? 'Os tokens ativos desta integração voltam a ser aceitos. Deseja continuar?'
              : 'Todos os tokens desta integração passam a ser recusados (401) até a integração ser ativada de novo. Deseja continuar?',
            onOk: () =>
              run('Salvando...', async () => {
                await IntegrationHttp.updateTpIntegration(tpId, integration.id, {
                  active: activating,
                })
                await reload()
              }),
          },
        }))
      },

      onCredentialsClicked(integration) {
        setState(s => ({ ...s, credentialsIntegrationId: integration.id }))
      },

      onCredentialsClosed() {
        setState(s => ({ ...s, credentialsIntegrationId: null }))
      },

      onGenerateCredential(integration, description) {
        return run('Gerando token...', async () => {
          const { token } = await IntegrationHttp.createIntegrationCredential(
            tpId,
            integration.id,
            description,
          )
          await reload()
          setState(s => ({ ...s, generatedToken: token }))
        })
      },

      onGeneratedTokenClosed() {
        setState(s => ({ ...s, generatedToken: null }))
      },

      onRevokeCredential(integration, credential) {
        setState(s => ({
          ...s,
          confirm: {
            title: 'Revogar token',
            content: `O token ${credential.prefix} passa a ser recusado imediatamente. Esta ação não pode ser desfeita. Deseja continuar?`,
            onOk: () =>
              run(
                'Revogando token...',
                async () => {
                  await IntegrationHttp.revokeIntegrationCredential(
                    tpId,
                    integration.id,
                    credential.id,
                  )
                  await reload()
                },
                'Token revogado',
              ),
          },
        }))
      },

      onConfirmCancel() {
        setState(s => ({ ...s, confirm: null }))
      },

      onCopyToken(token) {
        navigator.clipboard.writeText(token).then(
          () => dispatch(showMessageBox('Token copiado', null)),
          () => dispatch(showMessageBox(null, 'Erro ao copiar token')),
        )
      },
    }
  }, [tpId, dispatch])

  useEffect(() => {
    if (!tpId) return
    localDispatch.reload()
    getTiposDeArquivosDeImportacao()
      .then(fileTypes => setState(s => ({ ...s, fileTypes })))
      .catch(() => setState(s => ({ ...s, fileTypes: [] })))
  }, [tpId, localDispatch])

  // Fora do useMemo porque precisa da confirmação pendente do estado atual
  const onConfirmOk = () => {
    const pending = state.confirm
    setState(s => ({ ...s, confirm: null }))
    if (pending) pending.onOk()
  }

  // Só parceiros que ainda não têm integração com o TP podem ser escolhidos na criação
  const availablePartners = INTEGRATION_PARTNERS.filter(
    p => !state.integrations.some(i => i.partner === p.value),
  )
  const credentialsIntegration =
    state.integrations.find(i => i.id === state.credentialsIntegrationId) || null

  return {
    state,
    localDispatch: { ...localDispatch, onConfirmOk },
    availablePartners,
    credentialsIntegration,
  }
}
